import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import EmojiPicker from '@/components/EmojiPicker';
import { MediaGallery } from '@/components/CommunityMedia';
import { GrowerProfileModal } from '@/components/GardenStream';
import { ArrowLeft, Heart, Hand, MessageCircle, Send, Flag, Ban, UserPlus, UserCheck, Sparkles, Loader2, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STYLE_COLORS = {
  organic: 'bg-[#6B9F4A]/15 text-[#6B9F4A]',
  regenerative: 'bg-[#1A4331]/10 text-[#1A4331]',
  conventional: 'bg-[#8FB4D6]/20 text-[#5C7EA0]',
  mixed: 'bg-[#C9A85B]/15 text-[#B8974A]',
};

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function CommunityPost({ user }) {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [err, setErr] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [menu, setMenu] = useState(false);
  const [following, setFollowing] = useState(false);
  const [openProfile, setOpenProfile] = useState(null);

  const isPremium = !!(user?.subscription_active && ['premium', 'pro'].includes(user?.subscription_tier));

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/community/posts/${postId}`, { withCredentials: true }),
        axios.get(`${API}/community/posts/${postId}/comments`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setPost(p.data);
      setComments(c.data || []);
      if (p.data?.author_public && p.data?.author_id && p.data.author_id !== 'ai_garden_coach') {
        const fs = await axios.get(`${API}/community/follow-status/${p.data.author_id}`, { withCredentials: true }).catch(() => null);
        if (fs) setFollowing(!!fs.data.following);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load this post');
    }
  }, [postId]);
  useEffect(() => { load(); }, [load]);

  const toggleHelpful = async () => {
    const r = await axios.post(`${API}/community/posts/${postId}/helpful`, {}, { withCredentials: true }).catch(() => null);
    if (r) setPost((p) => ({ ...p, viewer_helpful: r.data.helpful, helpful_count: r.data.helpful_count }));
  };
  const toggleNudge = async () => {
    const r = await axios.post(`${API}/community/posts/${postId}/nudge`, {}, { withCredentials: true }).catch(() => null);
    if (r) setPost((p) => ({ ...p, viewer_nudged: r.data.nudged, nudge_count: r.data.nudge_count }));
  };
  const toggleFollow = async () => {
    if (!post?.author_id) return;
    try {
      if (following) { await axios.delete(`${API}/community/follow/${post.author_id}`, { withCredentials: true }); setFollowing(false); }
      else { await axios.post(`${API}/community/follow`, { user_id: post.author_id }, { withCredentials: true }); setFollowing(true); }
    } catch (e) { /* noop */ }
  };
  const submitComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true); setNotice('');
    try {
      const r = await axios.post(`${API}/community/posts/${postId}/comments`, { body: text.trim() }, { withCredentials: true });
      setText('');
      if (r.data.under_review) setNotice('Your comment is under review and will appear once approved.');
      load();
    } catch (e) { /* noop */ } finally { setBusy(false); }
  };
  const report = async () => {
    await axios.post(`${API}/community/report`, { target_type: 'post', target_id: postId, reason: 'Reported from post detail' }, { withCredentials: true }).catch(() => {});
    setNotice('Thanks — our team will review this.'); setMenu(false);
  };
  const block = async () => {
    if (!post?.author_id) return;
    await axios.post(`${API}/community/block`, { user_id: post.author_id }, { withCredentials: true }).catch(() => {});
    setNotice('Grower blocked — their content is now hidden.'); setMenu(false);
    setTimeout(() => navigate('/community'), 900);
  };

  const canFollow = post?.author_public && post?.author_id && !post?.is_self && post?.author_id !== 'ai_garden_coach' && post?.author_id !== user?.user_id;

  return (
    <Layout user={user}>
      <div className="max-w-2xl mx-auto">
        {/* Sticky back bar — premium glass */}
        <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-2 backdrop-blur-md bg-[#E5EBDD]/80 border-b border-[#1A4331]/10 flex items-center justify-between">
          <button onClick={() => navigate('/community')} data-testid="post-back-button" className="flex items-center gap-2 text-[#1A4331] font-semibold hover:opacity-70 transition-opacity">
            <ArrowLeft className="w-5 h-5" /> Back to feed
          </button>
          <div className="relative">
            <button onClick={() => setMenu((m) => !m)} data-testid="post-menu-button" className="text-[#7A6B57] hover:text-[#1A4331] text-xl leading-none px-2">⋯</button>
            {menu && (
              <div className="absolute right-0 top-9 bg-white rounded-xl shadow-xl border border-[#1A4331]/10 py-1 z-30 w-44">
                <button onClick={report} data-testid="report-post-button" className="w-full text-left px-3 py-2 text-sm text-[#C66B4A] hover:bg-[#F0F2EB] flex items-center gap-2"><Flag className="w-3.5 h-3.5" /> Report post</button>
                {canFollow && <button onClick={block} data-testid="block-grower-button" className="w-full text-left px-3 py-2 text-sm text-[#C66B4A] hover:bg-[#F0F2EB] flex items-center gap-2"><Ban className="w-3.5 h-3.5" /> Block grower</button>}
              </div>
            )}
          </div>
        </div>

        {err ? (
          <div className="bg-[#FAF6EC] rounded-3xl p-12 text-center border border-[#1A4331]/8">
            <p className="text-[#7A6B57]">{err}</p>
            <button onClick={() => navigate('/community')} className="mt-4 text-sm font-semibold text-[#1A4331]">← Back to community</button>
          </div>
        ) : !post ? (
          <div className="flex justify-center py-24"><div className="w-12 h-12 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" /></div>
        ) : (
          <div className="pb-24" data-testid="post-detail-page">
            {/* Media hero */}
            <MediaGallery photoUrls={post.photo_urls} videoUrl={post.video_url} cropName={post.crop_name} postId={post.id} tall />

            {/* Author + follow */}
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button onClick={() => post.author_public && post.author_id && setOpenProfile(post.author_id)} className="w-11 h-11 rounded-full bg-[#6B9F4A]/20 flex items-center justify-center text-[#1A4331] font-bold flex-shrink-0">
                {post.display_name?.[0]}
              </button>
              <div className="flex-1 min-w-0">
                <button onClick={() => post.author_public && post.author_id && setOpenProfile(post.author_id)} data-testid="post-author" className={`font-semibold text-[#1A4331] ${post.author_public ? 'hover:underline' : 'cursor-default'}`}>{post.display_name}</button>
                <p className="text-xs text-[#7A6B57]">{post.region_label || post.region} · {timeAgo(post.created_at)}</p>
              </div>
              {canFollow && (
                <button onClick={toggleFollow} data-testid="follow-button" className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-colors ${following ? 'bg-[#6B9F4A]/15 text-[#1A4331]' : 'bg-[#1A4331] text-[#FAF6EC] hover:bg-[#0F2D1F]'}`}>
                  {following ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
                </button>
              )}
            </div>

            {/* Title + value */}
            <div className="mt-4 bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10">
              <div className="flex items-center gap-2 flex-wrap">
                {post.growing_style && <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${STYLE_COLORS[post.growing_style] || 'bg-[#E5EBDD] text-[#7A6B57]'}`}>{post.growing_style}</span>}
                {post.is_expert && <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#C9A85B]/20 text-[#B8974A] flex items-center gap-1"><Sparkles className="w-3 h-3" /> Expert tip</span>}
              </div>
              <h1 className="text-3xl font-bold text-[#1A4331] mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {post.title || post.crop_name} {post.variety && <span className="text-[#7A6B57] text-xl">· {post.variety}</span>}
              </h1>
              {post.harvest_quantity && (
                <p className="text-sm text-[#2A3528] mt-2">
                  Harvested {post.harvest_quantity}{post.harvest_unit === 'kg' ? 'kg' : ` ${post.harvest_unit}`}
                  {post.estimated_value_low && post.estimated_value_high && <span className="text-[#C9A85B] font-semibold"> · ${post.estimated_value_low}–${post.estimated_value_high}</span>}
                </p>
              )}
              {post.note && <p className="text-[#2A3528] mt-3 leading-relaxed">{post.note}</p>}

              {/* Reaction bar */}
              {!post.is_expert && (
                <div className="flex items-center gap-2 mt-5 pt-4 border-t border-[#1A4331]/10 flex-wrap">
                  <button onClick={toggleHelpful} data-testid="helpful-button" className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-colors ${post.viewer_helpful ? 'bg-[#C66B4A]/15 text-[#C66B4A]' : 'bg-white text-[#7A6B57] hover:text-[#C66B4A]'}`}>
                    <Heart className={`w-4 h-4 ${post.viewer_helpful ? 'fill-[#C66B4A]' : ''}`} /> Helpful{post.helpful_count ? ` ${post.helpful_count}` : ''}
                  </button>
                  <button onClick={toggleNudge} data-testid="nudge-button" className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-colors ${post.viewer_nudged ? 'bg-[#C9A85B] text-[#1A4331]' : 'bg-white text-[#7A6B57] hover:text-[#C9A85B]'}`}>
                    <Hand className="w-4 h-4" /> Nudge{post.nudge_count ? ` ${post.nudge_count}` : ''}
                  </button>
                  <span className="flex items-center gap-1.5 text-sm text-[#7A6B57] ml-auto"><MessageCircle className="w-4 h-4" /> {post.comment_count || 0}</span>
                </div>
              )}
            </div>

            {/* Comments */}
            {!post.is_expert && (
              <div className="mt-4 bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10">
                <h3 className="text-lg font-semibold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Comments</h3>
                {notice && <div className="mb-3 text-xs bg-[#C9A85B]/15 text-[#1A4331] rounded-xl px-3 py-2" data-testid="post-notice">{notice}</div>}
                {!isPremium ? (
                  <div className="text-center py-6" data-testid="upgrade-inline">
                    <Award className="w-8 h-8 text-[#C9A85B] mx-auto mb-2" />
                    <p className="text-sm text-[#1A4331] font-semibold">Join the club to read and write comments</p>
                    <button onClick={() => navigate('/subscription')} className="mt-3 bg-[#C9A85B] hover:bg-[#B8974A] text-[#1A4331] font-semibold px-5 py-2.5 rounded-full text-sm">Upgrade to Join the Club</button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-4">
                      {comments.length === 0 ? (
                        <p className="text-sm text-[#7A6B57] italic">No comments yet — be the first to say something kind.</p>
                      ) : comments.map((c) => (
                        <div key={c.id} className="flex gap-2.5" data-testid={`comment-${c.id}`}>
                          <div className="w-8 h-8 rounded-full bg-[#6B9F4A]/20 flex items-center justify-center text-[#1A4331] text-xs font-bold flex-shrink-0">{c.display_name?.[0]}</div>
                          <div className="flex-1 bg-white rounded-2xl px-3 py-2">
                            <div className="flex items-center gap-2">
                              <button onClick={() => c.author_public && c.author_id && setOpenProfile(c.author_id)} className="text-xs font-semibold text-[#1A4331]">{c.display_name}</button>
                              <span className="text-[10px] text-[#7A6B57]">{timeAgo(c.created_at)}</span>
                            </div>
                            <p className="text-sm text-[#2A3528] mt-0.5">{c.body}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <form onSubmit={submitComment} className="flex items-center gap-2">
                      <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" data-testid="comment-input" className="flex-1 px-4 py-2.5 bg-white rounded-full border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-sm" />
                      <EmojiPicker testId="comment-emoji" onSelect={(e) => setText((t) => t + e)} />
                      <button type="submit" disabled={busy || !text.trim()} data-testid="comment-submit" className="bg-[#1A4331] text-[#FAF6EC] rounded-full p-2.5 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
                    </form>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {openProfile && <GrowerProfileModal userId={openProfile} onClose={() => setOpenProfile(null)} />}
    </Layout>
  );
}

export default CommunityPost;
